// DTOs/schemas do módulo "planos e cobrança". Este arquivo pertence ao pacote `cobranca`.
import { z } from "zod";

import type { StatusConta } from "./enums";

/** Plano como a landing e o seletor de planos o enxergam (`GET /publico/planos`). */
export interface PlanoPublico {
  /** publicId (cuid). */
  id: string;
  nome: string;
  precoCentavos: number;
  limiteNumeros: number;
  /** Lista descritiva para exibição. */
  recursos: string[];
  recomendado: boolean;
}

/** Formas de pagamento da assinatura. `UNDEFINED` = o cliente escolhe na página do Asaas. */
export const FORMAS_PAGAMENTO = ["PIX", "BOLETO", "CARTAO", "UNDEFINED"] as const;
export type FormaPagamento = (typeof FORMAS_PAGAMENTO)[number];

const soDigitos = (v: string) => v.replace(/\D/g, "");

/** CPF (11) ou CNPJ (14 dígitos), com dígitos verificadores válidos. */
export function documentoValido(valor: string): boolean {
  const d = soDigitos(valor);
  if (d.length !== 11 && d.length !== 14) return false;
  if (/^(\d)\1+$/.test(d)) return false;
  const dv = (base: string, pesos: number[]): number => {
    const soma = base.split("").reduce((acc, n, i) => acc + Number(n) * (pesos[i] as number), 0);
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };
  if (d.length === 11) {
    const p1 = [10, 9, 8, 7, 6, 5, 4, 3, 2];
    const p2 = [11, ...p1];
    const d1 = dv(d.slice(0, 9), p1);
    const d2 = dv(d.slice(0, 9) + d1, p2);
    return d === `${d.slice(0, 9)}${d1}${d2}`;
  }
  const p1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const p2 = [6, ...p1];
  const d1 = dv(d.slice(0, 12), p1);
  const d2 = dv(d.slice(0, 12) + d1, p2);
  return d === `${d.slice(0, 12)}${d1}${d2}`;
}

export const zDocumento = z
  .string()
  .trim()
  .transform(soDigitos)
  .refine(documentoValido, "Informe um CPF ou CNPJ válido.");

/** `POST /cobranca/assinatura`. `documento` só é exigido se a conta ainda não tem CPF/CNPJ. */
export const zAssinarPlano = z.object({
  planoId: z.string().trim().min(1, "Escolha um plano."),
  formaPagamento: z.enum(FORMAS_PAGAMENTO).default("UNDEFINED"),
  documento: zDocumento.optional(),
});
export type AssinarPlanoInput = z.input<typeof zAssinarPlano>;
export type AssinarPlano = z.output<typeof zAssinarPlano>;

/** `PUT /cobranca/plano` (upgrade imediato / downgrade quando couber). */
export const zTrocarPlano = z.object({
  planoId: z.string().trim().min(1, "Escolha um plano."),
});
export type TrocarPlano = z.infer<typeof zTrocarPlano>;

export interface FaturaResumo {
  /** Id do pagamento no Asaas (opaco; a PK interna nunca sai). */
  id: string;
  valorCentavos: number;
  /** Status do Asaas espelhado: PENDING, RECEIVED, CONFIRMED, OVERDUE, REFUNDED... */
  status: string;
  /** ISO (data). */
  vencimento: string;
  pagoEm: string | null;
  linkPagamento: string | null;
  /** PENDING ou OVERDUE: a tela destaca e mostra "Pagar agora". */
  emAberto: boolean;
}

/** `GET /cobranca`. */
export interface ResumoCobranca {
  plano: Omit<PlanoPublico, "recursos" | "recomendado"> | null;
  status: StatusConta;
  /** ISO. Só faz sentido em `trialing`. */
  trialEndsAt: string | null;
  /** Já existe assinatura no Asaas? */
  assinada: boolean;
  /** A conta já informou CPF/CNPJ (o formulário de assinatura só pede se faltar). */
  temDocumento: boolean;
  /** "2 de 3". */
  uso: { usados: number; limite: number };
  /** ISO. Fatura em aberto mais próxima; em trial sem fatura, o fim do trial. */
  proximoVencimento: string | null;
  pastDueDesde: string | null;
  suspensaEm: string | null;
  /** Fatura em aberto mais antiga (a que deve ser paga primeiro), se houver. */
  faturaEmAberto: FaturaResumo | null;
  faturas: FaturaResumo[];
  /** Upgrade aguardando pagamento ou downgrade agendado. */
  trocaPendente: TrocaPlanoResumo | null;
  /** Últimas trocas (inclui a pendente). */
  trocas: TrocaPlanoResumo[];
}

/** Resposta de `POST /cobranca/assinatura`. */
export interface RespostaAssinatura {
  plano: PlanoPublico;
  formaPagamento: FormaPagamento;
  /** Link da fatura/página de pagamento do Asaas. Pode vir nulo se o Asaas ainda a está gerando. */
  linkPagamento: string | null;
  /** ISO (data) do primeiro vencimento. */
  primeiroVencimento: string;
}

/** Troca de plano pendente (upgrade a pagar ou downgrade agendado) ou do histórico. */
export interface TrocaPlanoResumo {
  id: string; // TrocaPlano.publicId
  tipo: "upgrade" | "downgrade";
  status: "PENDENTE" | "APLICADA" | "CANCELADA" | "EXPIRADA";
  planoOrigem: string;
  planoDestino: string;
  /** Upgrade: valor da cobrança avulsa. */
  valorCentavos: number | null;
  linkPagamento: string | null;
  /** ISO. Downgrade: quando passa a valer. Upgrade: vencimento da cobrança. */
  efetivaEm: string | null;
  criadoEm: string;
}

/** `GET /cobranca/plano/previa?planoId=`: o que acontece se o OWNER confirmar a troca. Não grava nada. */
export interface PreviaTrocaPlano {
  plano: PlanoPublico;
  tipo: "upgrade" | "downgrade";
  situacao: "aplicado" | "pendente_pagamento" | "agendado";
  /** `pendente_pagamento`: valor da cobrança avulsa pró-rata. */
  valorCentavos: number | null;
  /** ISO (data). `agendado`: quando o plano muda. */
  efetivaEm: string | null;
}

/** Resposta de `PUT /cobranca/plano`. */
export interface RespostaTrocaPlano {
  plano: PlanoPublico;
  /** `upgrade` | `downgrade`. */
  tipo: "upgrade" | "downgrade";
  /**
   * `aplicado`: já vale (trial). `pendente_pagamento`: upgrade aguardando a cobrança avulsa.
   * `agendado`: downgrade no próximo vencimento.
   */
  situacao: "aplicado" | "pendente_pagamento" | "agendado";
  /** Limite que vale AGORA (o novo só em `aplicado`). */
  limiteNumeros: number;
  /** Upgrade/downgrade pendente criado por esta chamada; null quando `aplicado`. */
  troca: TrocaPlanoResumo | null;
}

/** Configuração pública (sem login): o que a landing e o cadastro precisam saber. */
export interface ConfiguracaoPublica {
  /** Duração do trial em dias (editável pelo SUPERADMIN). */
  trialDias: number;
  /** OAuth Client ID do Google; null → o botão "Entrar com Google" não aparece. */
  googleClientId: string | null;
}
